import { EnvironmentProviders, inject, InjectionToken, makeEnvironmentProviders, Provider, Type } from "@angular/core";

/**
 * interfaces
 */
import { 
    IProvideMqttConfig,
    IMqttConnectionOptions,
    IMqttAuthProviderSnapshot,
    IProfileConfig,
} from "../interfaces";

/**
 * services
 */
import { MqttCoreService } from "../mqtt-core.service";
import { MqttClientService } from "../mqtt-client.service";

/**
 * Injection token for MQTT Connection Options.
 * @type {InjectionToken<IMqttConnectionOptions>}
 */
export const MQTT_OPTIONS = new InjectionToken<IMqttConnectionOptions>('MQTT_OPTIONS');

/**
 * Injection token for MQTT Authentication Provider Service.
 * @type {InjectionToken<Type<IMqttAuthProviderSnapshot>>}
 */
export const MQTT_OPTIONS_AUTH_PROVIDER = new InjectionToken<Type<IMqttAuthProviderSnapshot>>('MQTT_AUTH_PROVIDER');

/**
 * Injection token for MQTT Profiles configuration.
 * @type {InjectionToken<IProfileConfig[]>}
 */
export const MQTT_PROFILES = new InjectionToken<IProfileConfig[]>('MQTT_PROFILES');


/**
 * Registers the MQTT Core Services at the root application level.
 * Use this in your `app.config.ts` or `AppModule`.
 * 
 * @param {IProvideMqttConfig} config - The configuration object.
 * @returns {EnvironmentProviders} The set of providers for the Angular environment.
 * 
 * @example
 * ```typescript
 * provideMqttCore({
 *   options: { url: 'wss://broker.com', connectionId: 'main' },
 *   authProvider: { provide: AuthService },
 *   profiles: [ ... ]
 * })
 * ```
 */
export const provideMqttCore = (config: IProvideMqttConfig): EnvironmentProviders => {
    
    // If no options are provided, throw an error
    if (config?.options == null) {
        throw new Error('MQTT options are required');
    }

    // If no authentication provider is provided, throw an error
    if (config?.authProvider == null) {
        throw new Error('MQTT authentication provider is required');
    }

    // If no connection ID is provided, set it to 'default'
    if (config?.options.connectionId == null) {
        config.options.connectionId = 'default';
    }

    return makeEnvironmentProviders([
        MqttCoreService,
        {
            provide: MQTT_OPTIONS,
            useValue: config.options,
        },
        {
            provide: MQTT_OPTIONS_AUTH_PROVIDER,
            useFactory: () => {
                return inject(config.authProvider.provide);
            },
        },
        {
            provide: MQTT_PROFILES,
            useValue: config?.profiles ?? [],
        },
    ]);
};

/**
 * Registers a new `MqttClientService` instance for a specific Component or Module.
 * Useful for lazy-loaded features that need a separate client instance or configuration override.
 * Inherits configuration from the parent `provideMqttCore` if not explicitly overridden.
 * 
 * @param {Partial<IProvideMqttConfig>} [config] - Optional configuration overrides.
 * @returns {Provider[]} The providers array.
 * 
 * @example
 * ```typescript
 * @Component({
 *   providers: [
 *     provideMqttClient({ profiles: [ featureProfile ] })
 *   ]
 * })
 * ```
 */
export const provideMqttClient = (config?: Partial<IProvideMqttConfig>): Provider[] => {

    return [
        MqttClientService,
        {
            provide: MQTT_OPTIONS,
            useFactory: () => {

                // Get the parent MQTT options
                const parent = inject(MQTT_OPTIONS, { skipSelf: true });

                // If config is provided and connection ID is provided, use it
                if (config != null && config.options?.connectionId != null) {
                    config.options.connectionId = parent.connectionId;
                }
                
                
                return { ...parent, ...config?.options };
            },
          },
          {
            provide: MQTT_OPTIONS_AUTH_PROVIDER,
            useFactory: () => {

                // Get the parent authentication provider
                const parent = inject(MQTT_OPTIONS_AUTH_PROVIDER, { skipSelf: true });

                // If no parent or config authentication provider is provided, throw an error
                if (parent == null && config?.authProvider?.provide == null) {
                    throw new Error('MQTT authentication provider is required in parent or config');
                }
                
                // If no config or authProvider specified, use parent
                if (config?.authProvider?.provide == null) {
                    return parent;
                }

                // Inject the specified auth provider
                return inject(config.authProvider.provide);
            },
        },
        {
            provide: MQTT_PROFILES,
            useFactory: () => {

                // Get the parent MQTT profiles
                const parent = inject(MQTT_PROFILES, { skipSelf: true, optional: true });
                const profiles = config?.profiles ?? [];

                // If parent is an array, merge the profiles
                if (Array.isArray(parent)) {
                    return [...parent, ...profiles];
                }

                // If config is an array, return the config
                if (Array.isArray(profiles)) {
                    return profiles;
                }

                // If both parent and config are not arrays, return an empty array
                return [];
            },
        },
    ];
};
